import axios from 'axios';
import ServicePath from '../Webservice/Webservice';
import { AesEncrypt, AesDecrypt } from '../crypto';
import store from '../store/store';
import { logout } from '../store/authSlice';
import toast from 'react-hot-toast';
import { isTokenExpired } from '../utils/jwt';
const api = axios.create({
  baseURL: `${ServicePath}/api`,
  timeout: 15000,
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      if (isTokenExpired(token) && !config.url?.includes('/auth/login')) {
        store.dispatch(logout());
        return Promise.reject(new Error('Session expired'));
      }
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Encrypt the request payload if it exists, is an object, and is not FormData
    if (config.data && !(config.data instanceof FormData) && typeof config.data === 'object') {
      config.data = AesEncrypt(config.data);
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Handle network errors and 401 globally
api.interceptors.response.use(
  (response) => {
    // Decrypt successful responses if they are encrypted
    if (response.data && typeof response.data === 'object' && response.data.data && response.data.iv) {
      try {
        response.data = AesDecrypt(response.data);
      } catch (error) {
        console.error("Failed to decrypt response:", error);
      }
    }
    return response;
  },
  (error) => {
    // Decrypt error responses if they are encrypted
    if (error.response && error.response.data && typeof error.response.data === 'object' && error.response.data.data && error.response.data.iv) {
      try {
        error.response.data = AesDecrypt(error.response.data);
      } catch (e) {
        console.error("Failed to decrypt error response:", e);
      }
    }

    // Check if it's a network error (no response from server)
    if (!error.response && error.isAxiosError) {
      error.response = {
        data: {
          message: "We're having trouble connecting right now. Please check your internet connection and try again in a moment."
        }
      };
    }

    if ((error.response?.status === 401 || error.response?.status === 403) && 
        !error.config?.url?.includes('/auth/login') && 
        !error.config?.url?.includes('/seller/change-password')) {
      // Dispatch Redux action instead of hard reloading page
      // This will clear state, clear localStorage, and PrivateRoute will immediately redirect
      store.dispatch(logout());
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  updatePassword: (data) => api.post('/auth/update-password', data),
  sendOTP: (data) => api.post('/auth/send-otp', data),
  verifyOTP: (data) => api.post('/auth/verify-otp', data),
  getSellerStatus: (id) => api.get(`/auth/seller-status/${id}`),
};

// Seller
export const sellerAPI = {
  getProfile: () => api.get('/seller/profile'),
  updateProfile: (data) => api.put('/seller/profile', data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  updateStoreInfo: (data) => api.put('/seller/storeinfo', data),
  uploadDocument: (data) => api.post('/seller/upload-document', data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  uploadProfileImage: (data) => api.post('/seller/upload-profile-image', data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  removeProfileImage: () => api.put('/seller/profile', { storeLogo: "" }),
  updateBankDetails: (data) => api.put('/seller/bankdetails', data),
  updateLocation: (data) => api.put('/seller/location', data),
  updateShippingRules: (data) => api.put('/seller/shipping-rules', data),
  changePassword: (data) => api.put('/seller/change-password', data),
  submitVerification: () => api.post('/seller/submit-verification'),
  getPlatformFees: () => api.get('/config/fees'),
};

// Products
export const productAPI = {
  getAll: (data) => api.post('/get-seller-products', data),
  getById: (id) => api.get(`/products/${id}`),
  getEditingProduct: (id) => api.post('/get-editing-product', { id }),
  create: (data) => api.post('/products', data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (data) => api.post(`/product-update`, data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteProduct: (data) => api.post(`/products-delete`, data),
  bulkUpdateStock: (params) => api.post('/products/bulk-stock', { params }),
  uploadCSV: (formData) => api.post('/products/upload-csv', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }),
  getAdminProductsDropdown: (data) => api.post('/admin-products-dropdown', data),
  getAdminTempDownloadSelector: (data) => api.post('/admintemp-download-selector', data),
  getAdminProducts: (data) => api.post('/adminproducts', data),
  getInventoryList: (data) => api.post('/inventory-list', data),
  getLowStockProducts: (data) => api.post('/low-stock-products', data),
  getProductsTemplateData: () => api.get('/products-template-data'),
};

// Orders
export const orderAPI = {
  getOrderList: (data) => api.post('/order_list', data),
  getOrderDetails: (data) => api.post('/customer_order_details', data),
  getAll: (params) => api.get('/orders', { params }),
  getById: (id) => api.get(`/orders/${id}`),
  updateStatus: (id, data) => api.put(`/orders/${id}/status`, data),
  pickupProduct: (data) => api.post('/pickup-product', data),
  outForDelivery: (data) => api.post('/out-for-delivery', data),
  verifyDeliveryOtp: (data) => api.post('/verify-delivery-otp', data),
  getPickupList: (data) => api.post('/get-pickup-list', data),
  getSettlementSplit: (data) => api.post('/get-settlement-split', data),
  updateReturnStatus: (data) => api.post('/update-return-status', data),
  getReturnList: (data) => api.post('/return-list', data),
  verifyReturnPickupOtp: (data) => api.post('/verify-return-pickup', data),
  getNotifications: (params) => api.get('/notifications', { params }),
  markNotificationViewed: (data) => api.post('/mark_notification_viewed', data),
  markAllNotificationsViewed: () => api.post('/mark_all_notifications_viewed'),
  downloadReceipt: (orderId, type = 'invoice') => api.get(`/seller-invoice/${orderId}`, { params: { type }, responseType: 'blob' }),
  downloadSellerInvoice: (orderId) => api.get(`/seller-invoice-download/${orderId}`, { responseType: 'blob' }),
  downloadSettlementBill: (orderId, productId) => api.get(`/settlement_bill/${orderId}/${productId}`, { responseType: 'blob' }),
  downloadSettlementCsv: (orderId) => api.get(`/settlement_csv/${orderId}`, { responseType: 'blob' }),
  downloadBulkSettlementCsv: (data) => api.post('/bulk_settlement_csv', data, { responseType: 'blob' }),
};

// Analytics
export const analyticsAPI = {
  getStats: () => api.get('/analytics/stats'),
  getRevenue: (year) => api.get('/analytics/revenue', { params: { year } }),
  getBestSellers: () => api.get('/analytics/best-sellers'),
};

// Tickets
export const ticketAPI = {
  create: (data) => api.post('/tickets/create', data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getList: (data) => api.post('/tickets/list', data),
  getDetails: (data) => api.post('/tickets/details', data),
  addMessage: (data) => api.post('/tickets/message', data),
  updateStatus: (data) => api.post('/tickets/update-status', data),
  getRelatedSolutions: (data) => api.post('/tickets/related-solutions', data),
  getCategories: () => api.get('/tickets/categories'),
  checkLimit: (data) => api.post('/tickets/check-limit', data),
};

export default api;
